import { createSlice } from '@reduxjs/toolkit';
import axios from 'utils/axios';

function toUnixSeconds(dateStr) {
  return Math.floor(new Date(dateStr).getTime() / 1000);
}

// A segment's `display` can be a boolean (e.g. isReactivation → true/false). Many chart/table
// renderers call `display.toLowerCase()` (optional chaining does NOT guard a boolean), so normalize
// display to a string as it enters the store — no consumer downstream can then crash on it.
function normalizeSegmentsDisplay(segments) {
  if (!Array.isArray(segments)) return segments;
  return segments.map((s) =>
    s && s.display != null && typeof s.display !== 'string' ? { ...s, display: String(s.display) } : s,
  );
}

/**
 * Cohorted endpoints may return either:
 * - rows array directly
 * - { rows: [...] }
 * - { data: [...] }
 * Keep reducers resilient so loading state always resolves.
 */
function normalizeCohortRows(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}


// Backend returns pre-bucketed rows with rates already computed — just pass through.
function aggregateConversionRows(rows) {
  return (Array.isArray(rows) ? rows : []).filter((r) => r?.date);
}

function aggregateTrialRows(rows) {
  return (Array.isArray(rows) ? rows : []).filter((r) => r?.date);
}

/** Whether the cohort period is still inside the conversion window (partial / incomplete while data is settling). */
function rowCohortEdgeIncomplete(r) {
  if (r == null) return false;
  if (r.incomplete === true) return true;
  if (r.confidence === 'partial' || r.confidence === 'low') return true;
  if (r.is_complete === false) return true;
  if (r.cohort_maturity === 'incomplete' || r.maturity === 'incomplete') return true;
  return false;
}

const SEGMENTED_CHART_TOP_N = 10;

function buildConversionChartData(rows) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const isSegmented = safeRows.some((r) => r?.segment != null);

  if (isSegmented) {
    // Rank segments by total new_users across all dates; take top N.
    const segmentTotals = new Map();
    safeRows.forEach((r) => {
      const seg = r.segment;
      segmentTotals.set(seg, (segmentTotals.get(seg) || 0) + (r.new_users || 0));
    });
    const topSegments = [...segmentTotals.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, SEGMENTED_CHART_TOP_N)
      .map(([seg]) => seg);
    const topSet = new Set(topSegments);

    // Build a map: ts → { [segment]: conversion_rate }
    const dateMap = new Map();
    const incompleteByTs = new Map();
    safeRows.forEach((r) => {
      const ts = toUnixSeconds(r.date);
      if (!dateMap.has(ts)) dateMap.set(ts, {});
      if (topSet.has(r.segment)) {
        dateMap.get(ts)[r.segment] = r.conversion_rate ?? 0;
      }
      if (rowCohortEdgeIncomplete(r)) incompleteByTs.set(ts, true);
    });

    const sortedTs = [...dateMap.keys()].sort((a, b) => a - b);
    const segments = topSegments.map((seg, i) => ({ display: seg, index: i, chartable: true }));
    const values = sortedTs.map((ts) => [ts, ...topSegments.map((seg) => dateMap.get(ts)?.[seg] ?? 0)]);
    const pointIncomplete = sortedTs.map((ts) => incompleteByTs.has(ts));

    return { segments, values, pointIncomplete };
  }

  // Non-segmented path (single conversion rate series).
  const segments = [{ display: 'Conversion Rate', index: 0, chartable: true }];
  const values = safeRows.map((r) => [toUnixSeconds(r.date), r.conversion_rate ?? 0]);
  const pointIncomplete = safeRows.map((r) => rowCohortEdgeIncomplete(r));
  return { segments, values, pointIncomplete };
}


const initialState = {
  options: {
    startDate: null,
    endDate: null,
    resolution: '',
    filters: { segment: '' },
  },

  meta: {
    isLoading: false,
    error: null,
    /** appId the cached meta belongs to + whether it finished loading (app-scoped cache guard). */
    appId: null,
    loaded: false,

    /** The app's first transaction date, so "All Time" can mean all time. */
    earliestEventDate: null,

    mediaSources: [],
    campaigns: [],
    keywords: [],
    stores: [],
    products: [],
    productDurations: [],
    countriesByActivity: [],
    acquisitionSources: [],
  },
  revenue: {
    isLoading: false,
    error: null,

    startDate: null,
    endDate: null,
    resolution: '',
    filters: {},

    segment: null,
    segments: [],
    summary: {},
    values: [],
  },
  mrr: {
    isLoading: false,
    error: null,

    startDate: null,
    endDate: null,
    resolution: '',
    filters: {},

    segment: null,
    segments: [],
    summary: null,
    values: [],
  },
  arr: {
    isLoading: false,
    error: null,

    startDate: null,
    endDate: null,
    resolution: '',
    filters: {},

    segment: null,
    segments: [],
    summary: null,
    values: [],
  },
  churn: {
    isLoading: false,
    error: null,

    startDate: null,
    endDate: null,
    resolution: '',
    filters: {},

    segment: null,
    segments: [],
    summary: null,
    values: [],
  },
  refundRate: {
    isLoading: false,
    error: null,

    startDate: null,
    endDate: null,
    resolution: '',
    filters: {},

    segment: null,
    segments: [],
    summary: null,
    values: [],
  },
  appStoreRefundRequests: {
    isLoading: false,
    error: null,

    startDate: null,
    endDate: null,
    resolution: '',
    filters: {},

    segment: null,
    segments: [],
    summary: null,
    values: [],
  },
  playStoreCancelReasons: {
    isLoading: false,
    error: null,

    startDate: null,
    endDate: null,
    resolution: '',
    filters: {},

    segment: null,
    segments: [],
    summary: null,
    values: [],
  },
  cancellationTiming: {
    isLoading: false,
    error: null,

    startDate: null,
    endDate: null,
    cancellationType: 'all',
    filters: {},
    segment: null,
    distribution: [],
    distributionSeries: null,
    summary: null,
    segments: [],
    values: [],
  },
  subscriptionStatus: {
    isLoading: false,
    error: null,

    startDate: null,
    endDate: null,
    resolution: 'days',
    measure: 'active_subscriptions',
    filters: {},

    segment: null,
    segments: [],
    summary: null,
    values: [],
  },
  conversionToPaying: {
    isLoading: false,
    error: null,

    startDate: null,
    endDate: null,
    resolution: '',
    filters: {},

    segment: null,
    segments: [],
    summary: null,
    values: [],
  },
  initialConversion: {
    isLoading: false,
    error: null,

    startDate: null,
    endDate: null,
    resolution: '',
    filters: {},

    segment: null,
    segments: [],
    summary: null,
    values: [],
  },
  trialConversion: {
    isLoading: false,
    error: null,

    startDate: null,
    endDate: null,
    resolution: '',
    filters: {},

    segment: null,
    segments: [],
    summary: null,
    values: [],
  },
  offerConversion: {
    isLoading: false,
    error: null,

    startDate: null,
    endDate: null,
    resolution: '',
    filters: {},

    segment: null,
    segments: [],
    summary: null,
    values: [],
  },
  reactivation: {
    isLoading: false,
    error: null,

    startDate: null,
    endDate: null,
    resolution: '',
    filters: {},

    segment: null,
    segments: [],
    summary: null,
    values: [],
  },
  activeTrials: {
    isLoading: false,
    error: null,

    startDate: null,
    endDate: null,
    resolution: '',
    filters: {},

    segment: null,
    segments: [],
    summary: null,
    snapshot: null,
    values: [],
  },
  newTrials: {
    isLoading: false,
    error: null,

    startDate: null,
    endDate: null,
    resolution: '',
    filters: {},

    segment: null,
    segments: [],
    summary: null,
    values: [],
  },
  activeTrialsMovement: {
    isLoading: false,
    error: null,

    startDate: null,
    endDate: null,
    resolution: '',
    filters: {},

    segment: null,
    segments: [],
    summary: null,
    values: [],
  },
  activeSubscribers: {
    isLoading: false,
    error: null,
    startDate: null,
    endDate: null,
    resolution: '',
    filters: {},
    segment: null,
    segments: [],
    summary: null,
    values: [],
  },
  activeSubscriptionsMovement: {
    isLoading: false,
    error: null,
    startDate: null,
    endDate: null,
    resolution: '',
    filters: {},
    segment: null,
    segments: [],
    summary: null,
    values: [],
  },
  cohortExplorer: {
    isLoading: false,
    error: null,
    startDate: null,
    endDate: null,
    resolution: '',
    revenueType: 'gross',
    cohortDefinition: '',
    measure: '',
    filters: {},
    data: null,
  },
  cohortExplorerRecoup: {
    isLoading: false,
    error: null,
    startDate: null,
    endDate: null,
    resolution: '',
    revenueType: 'gross',
    cohortDefinition: '',
    filters: {},
    data: null,
  },
  cohortedInitialConversion: {
    isLoading: false,
    error: null,
    startDate: null,
    endDate: null,
    resolution: '',
    conversionTimeframe: '30',
    segment: null,
    filters: {},
    segments: [],
    values: [],
    rawData: [],
    pointIncomplete: [],
  },
  cohortedTrialConversion: {
    isLoading: false,
    error: null,
    startDate: null,
    endDate: null,
    resolution: '',
    filters: {},
    segments: [],
    values: [],
    rawData: [],
    pointIncomplete: [],
  },
  cohortedIntroOfferConversion: {
    isLoading: false,
    error: null,
    startDate: null,
    endDate: null,
    resolution: '',
    filters: {},
    segments: [],
    values: [],
    rawData: [],
    pointIncomplete: [],
  },
  cohortedConversionToPaying: {
    isLoading: false,
    error: null,
    startDate: null,
    endDate: null,
    resolution: '',
    conversionTimeframe: '30',
    segment: null,
    filters: {},
    segments: [],
    values: [],
    rawData: [],
    pointIncomplete: [],
  },
  cohortedReactivation: {
    isLoading: false,
    error: null,
    startDate: null,
    endDate: null,
    resolution: '',
    conversionTimeframe: '30',
    cohortBasis: 'initial_conversions',
    segment: null,
    filters: {},
    segments: [],
    values: [],
    rawData: [],
    pointIncomplete: [],
  },
  autoRenewStatus: {
    isLoading: false,
    error: null,

    startDate: null,
    endDate: null,
    resolution: '',
    filters: {},

    values: [],
  },
  subscriptionRetention: {
    isLoading: false,
    error: null,

    startDate: null,
    endDate: null,
    resolution: '',
    period: '',
    filters: {},

    segment: null,
    segments: [],
    summary: null,
    values: [],
    percentageValues: [],
    extras: {},
  },
  subscriptionEvents: {
    isLoading: false,
    error: null,

    startDate: null,
    endDate: null,
    resolution: '',
    filters: {},

    segment: null,
    segments: [],
    summary: null,
    values: [],
    extras: {
      segment: null,
      segments: [],
      summary: { average: {}, total: {} },
      values: [],
    },
    breakdown: null,
  },
  trafficQuality: {
    isLoading: false,
    error: null,
    data: [],
  },
  adROI: {
    isLoading: false,
    error: null,
    startDate: null,
    endDate: null,
    filters: {},
    segment: null,
    data: [],
    summary: null,
    filterOptions: {
      countries: [],
      mediaSources: [],
      campaigns: [],
    },
    // Trend chart state
    chart: {
      isLoading: false,
      error: null,
      resolution: 'days',
      segments: [],
      summary: {},
      values: [],
    },
  },
  costETL: {
    isLoading: false,
    error: null,
    startDate: null,
    endDate: null,
    filters: {},
    segment: null,
    data: [],
    summary: null,
    filterOptions: {
      appIds: [],
      countries: [],
      mediaSources: [],
      campaigns: [],
      platforms: [],
    },
    availableDates: [],
    // Trend chart state
    chart: {
      isLoading: false,
      error: null,
      resolution: 'days',
      segments: [],
      summary: {},
      values: [],
    },
  },
};

const slice = createSlice({
  name: 'revenueManagementCharts',
  initialState,
  reducers: {
    updateStartDate(state, action) {
      const { chartKey, value } = action.payload;
      state[chartKey].startDate = value;
    },

    updateEndDate(state, action) {
      const { chartKey, value } = action.payload;
      state[chartKey].endDate = value;
    },

    resetFilters(state, action) {
      const { chartKey } = action.payload;

      state[chartKey].filters = {};
    },

    updateFilters(state, action) {
      const { chartKey, segment, ...filters } = action.payload;

      // Update segment if provided
      if (segment !== undefined) {
        state[chartKey].segment = segment || null;
      }

      // Merge filters; remove key when value is null/undefined/empty-string/empty-array
      const updatedFilters = { ...state[chartKey].filters };
      Object.keys(filters).forEach((key) => {
        const v = filters[key];
        if (v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) {
          delete updatedFilters[key];
        } else {
          updatedFilters[key] = v;
        }
      });
      state[chartKey].filters = updatedFilters;
    },

    updateResolution(state, action) {
      const { chartKey, value } = action.payload;
      state[chartKey].resolution = value;
    },

    updatePeriod(state, action) {
      const { chartKey, value } = action.payload;
      state[chartKey].period = value;
    },

    metaChartStartLoading(state, action) {
      state.meta.isLoading = true;
      state.meta.appId = action.payload; // target appId for in-flight/cache guards
    },

    metaChartHasError(state, action) {
      state.meta.error = action.payload;
      state.meta.isLoading = false;
    },

    metaChartSuccess(state, action) {
      // Drop a stale response if the app changed while this request was in flight.
      if (String(action.payload.appId) !== String(state.meta.appId)) return;
      state.meta.earliestEventDate = action.payload.earliestEventDate ?? null;
      state.meta.mediaSources = action.payload.mediaSources;
      state.meta.campaigns = action.payload.campaigns;
      state.meta.stores = action.payload.apps;
      state.meta.products = action.payload.products;
      state.meta.productDurations = action.payload.productDurations;
      state.meta.countriesByActivity = action.payload.countriesByActivity || [];
      state.meta.acquisitionSources = action.payload.acquisitionSources || [];
      state.meta.isLoading = false;
      state.meta.loaded = true;
    },

    revenueChartStartLoading(state) {
      state.revenue.isLoading = true;
    },

    revenueChartHasError(state, action) {
      state.revenue.isLoading = false;
      state.revenue.error = action.payload;
    },

    revenueChartSuccess(state, action) {
      // Only update segment if explicitly provided in response (not null/undefined)
      // This preserves user-selected segment when API doesn't return segment data
      if (action.payload.segment !== null && action.payload.segment !== undefined) {
        state.revenue.segment = action.payload.segment;
      }
      state.revenue.segments = action.payload.segments;
      state.revenue.summary = action.payload.summary;
      state.revenue.values = action.payload.values;

      state.revenue.isLoading = false;
    },

    mrrChartStartLoading(state) {
      state.mrr.isLoading = true;
    },

    mrrChartHasError(state, action) {
      state.mrr.isLoading = false;
      state.mrr.error = action.payload;
    },

    mrrChartSuccess(state, action) {
      state.mrr.segment = action.payload.segment;
      state.mrr.segments = action.payload.segments;
      state.mrr.summary = action.payload.summary;
      state.mrr.values = action.payload.values;

      state.mrr.isLoading = false;
    },

    arrChartStartLoading(state) {
      state.arr.isLoading = true;
    },

    arrChartHasError(state, action) {
      state.arr.isLoading = false;
      state.arr.error = action.payload;
    },

    arrChartSuccess(state, action) {
      state.arr.segment = action.payload.segment;
      state.arr.segments = action.payload.segments;
      state.arr.summary = action.payload.summary;
      state.arr.values = action.payload.values;

      state.arr.isLoading = false;
    },

    churnChartStartLoading(state) {
      state.churn.isLoading = true;
    },

    churnChartHasError(state, action) {
      state.churn.isLoading = false;
      state.churn.error = action.payload;
    },

    churnChartSuccess(state, action) {
      state.churn.segment = action.payload.segment;
      state.churn.segments = action.payload.segments;
      state.churn.summary = action.payload.summary;
      state.churn.values = action.payload.values;

      state.churn.isLoading = false;
    },

    refundRateChartStartLoading(state) {
      state.refundRate.isLoading = true;
    },

    refundRateChartHasError(state, action) {
      state.refundRate.isLoading = false;
      state.refundRate.error = action.payload;
    },

    refundRateChartSuccess(state, action) {
      state.refundRate.segment = action.payload.segment;
      state.refundRate.segments = action.payload.segments;
      state.refundRate.summary = action.payload.summary;
      state.refundRate.values = action.payload.values;

      state.refundRate.isLoading = false;
    },

    appStoreRefundRequestsChartStartLoading(state) {
      state.appStoreRefundRequests.isLoading = true;
    },

    appStoreRefundRequestsChartHasError(state, action) {
      state.appStoreRefundRequests.isLoading = false;
      state.appStoreRefundRequests.error = action.payload;
    },

    appStoreRefundRequestsChartSuccess(state, action) {
      state.appStoreRefundRequests.segment = action.payload.segment;
      state.appStoreRefundRequests.segments = action.payload.segments;
      state.appStoreRefundRequests.summary = action.payload.summary;
      state.appStoreRefundRequests.values = action.payload.values;

      state.appStoreRefundRequests.isLoading = false;
    },

    playStoreCancelReasonsChartStartLoading(state) {
      state.playStoreCancelReasons.isLoading = true;
    },

    playStoreCancelReasonsChartHasError(state, action) {
      state.playStoreCancelReasons.isLoading = false;
      state.playStoreCancelReasons.error = action.payload;
    },

    playStoreCancelReasonsChartSuccess(state, action) {
      state.playStoreCancelReasons.segment = action.payload.segment;
      state.playStoreCancelReasons.segments = action.payload.segments;
      state.playStoreCancelReasons.summary = action.payload.summary;
      state.playStoreCancelReasons.values = action.payload.values;

      state.playStoreCancelReasons.isLoading = false;
    },

    cancellationTimingChartStartLoading(state) {
      state.cancellationTiming.isLoading = true;
    },

    cancellationTimingChartHasError(state, action) {
      state.cancellationTiming.isLoading = false;
      state.cancellationTiming.error = action.payload;
    },

    cancellationTimingChartSuccess(state, action) {
      const p = action.payload;
      state.cancellationTiming.distribution = p.distribution;
      state.cancellationTiming.distributionSeries = p.distributionSeries ?? null;
      state.cancellationTiming.summary = p.summary;
      state.cancellationTiming.segments = p.segments ?? [];
      state.cancellationTiming.values = p.values ?? [];

      state.cancellationTiming.isLoading = false;
    },

    updateCancellationType(state, action) {
      const { chartKey, value } = action.payload;
      state[chartKey].cancellationType = value;
    },

    subscriptionStatusChartStartLoading(state) {
      state.subscriptionStatus.isLoading = true;
    },
    subscriptionStatusChartHasError(state, action) {
      state.subscriptionStatus.isLoading = false;
      state.subscriptionStatus.error = action.payload;
    },
    subscriptionStatusChartSuccess(state, action) {
      const p = action.payload;
      if (p.segment !== null && p.segment !== undefined) {
        state.subscriptionStatus.segment = p.segment;
      }
      state.subscriptionStatus.segments = p.segments ?? [];
      state.subscriptionStatus.summary = p.summary;
      state.subscriptionStatus.values = p.values ?? [];
      state.subscriptionStatus.isLoading = false;
    },
    updateSubscriptionStatusMeasure(state, action) {
      const { chartKey, value } = action.payload;
      state[chartKey].measure = value;
    },

    conversionToPayingChartStartLoading(state) {
      state.conversionToPaying.isLoading = true;
    },

    conversionToPayingChartHasError(state, action) {
      state.conversionToPaying.isLoading = false;
      state.conversionToPaying.error = action.payload;
    },

    conversionToPayingChartSuccess(state, action) {
      state.conversionToPaying.segment = action.payload.segment;
      state.conversionToPaying.segments = action.payload.segments;
      state.conversionToPaying.summary = action.payload.summary;
      state.conversionToPaying.values = action.payload.values;

      state.conversionToPaying.isLoading = false;
    },

    initialConversionChartStartLoading(state) {
      state.initialConversion.isLoading = true;
    },

    initialConversionChartHasError(state, action) {
      state.initialConversion.isLoading = false;
      state.initialConversion.error = action.payload;
    },

    initialConversionChartSuccess(state, action) {
      state.initialConversion.segment = action.payload.segment;
      state.initialConversion.segments = action.payload.segments;
      state.initialConversion.summary = action.payload.summary;
      state.initialConversion.values = action.payload.values;

      state.initialConversion.isLoading = false;
    },

    trialConversionChartStartLoading(state) {
      state.trialConversion.isLoading = true;
    },

    trialConversionChartHasError(state, action) {
      state.trialConversion.isLoading = false;
      state.trialConversion.error = action.payload;
    },

    trialConversionChartSuccess(state, action) {
      state.trialConversion.segment = action.payload.segment;
      state.trialConversion.segments = normalizeSegmentsDisplay(action.payload.segments);
      state.trialConversion.summary = action.payload.summary;
      state.trialConversion.values = action.payload.values;

      state.trialConversion.isLoading = false;
    },

    offerConversionChartStartLoading(state) {
      state.offerConversion.isLoading = true;
    },

    offerConversionChartHasError(state, action) {
      state.offerConversion.isLoading = false;
      state.offerConversion.error = action.payload;
    },

    offerConversionChartSuccess(state, action) {
      state.offerConversion.segment = action.payload.segment;
      state.offerConversion.segments = action.payload.segments;
      state.offerConversion.summary = action.payload.summary;
      state.offerConversion.values = action.payload.values;

      state.offerConversion.isLoading = false;
    },

    reactivationChartStartLoading(state) {
      state.reactivation.isLoading = true;
    },

    reactivationChartHasError(state, action) {
      state.reactivation.isLoading = false;
      state.reactivation.error = action.payload;
    },

    reactivationChartSuccess(state, action) {
      state.reactivation.segment = action.payload.segment;
      state.reactivation.segments = action.payload.segments;
      state.reactivation.summary = action.payload.summary;
      state.reactivation.values = action.payload.values;

      state.reactivation.isLoading = false;
    },

    activeTrialsChartStartLoading(state) {
      state.activeTrials.isLoading = true;
    },

    activeTrialsChartHasError(state, action) {
      state.activeTrials.isLoading = false;
      state.activeTrials.error = action.payload;
    },

    activeTrialsChartSuccess(state, action) {
      state.activeTrials.segment = action.payload.segment;
      state.activeTrials.segments = action.payload.segments;
      state.activeTrials.summary = action.payload.summary;
      state.activeTrials.snapshot = action.payload.snapshot ?? null;
      state.activeTrials.values = action.payload.values;

      state.activeTrials.isLoading = false;
    },

    newTrialsChartStartLoading(state) {
      state.newTrials.isLoading = true;
    },

    newTrialsChartHasError(state, action) {
      state.newTrials.isLoading = false;
      state.newTrials.error = action.payload;
    },

    newTrialsChartSuccess(state, action) {
      state.newTrials.segment = action.payload.segment;
      state.newTrials.segments = action.payload.segments;
      state.newTrials.summary = action.payload.summary;
      state.newTrials.values = action.payload.values;

      state.newTrials.isLoading = false;
    },

    activeTrialsMovementChartStartLoading(state) {
      state.activeTrialsMovement.isLoading = true;
    },

    activeTrialsMovementChartHasError(state, action) {
      state.activeTrialsMovement.isLoading = false;
      state.activeTrialsMovement.error = action.payload;
    },

    activeTrialsMovementChartSuccess(state, action) {
      state.activeTrialsMovement.segment = action.payload.segment;
      state.activeTrialsMovement.segments = action.payload.segments;
      state.activeTrialsMovement.summary = action.payload.summary;
      state.activeTrialsMovement.values = action.payload.values;

      state.activeTrialsMovement.isLoading = false;
    },

    activeSubscribersChartStartLoading(state) {
      state.activeSubscribers.isLoading = true;
    },
    activeSubscribersChartHasError(state, action) {
      state.activeSubscribers.isLoading = false;
      state.activeSubscribers.error = action.payload;
    },
    activeSubscribersChartSuccess(state, action) {
      state.activeSubscribers.segment = action.payload.segment;
      state.activeSubscribers.segments = action.payload.segments;
      state.activeSubscribers.summary = action.payload.summary;
      state.activeSubscribers.values = action.payload.values;

      state.activeSubscribers.isLoading = false;
    },

    activeSubscriptionsMovementChartStartLoading(state) {
      state.activeSubscriptionsMovement.isLoading = true;
    },

    activeSubscriptionsMovementChartHasError(state, action) {
      state.activeSubscriptionsMovement.isLoading = false;
      state.activeSubscriptionsMovement.error = action.payload;
    },

    activeSubscriptionsMovementChartSuccess(state, action) {
      state.activeSubscriptionsMovement.segment = action.payload.segment;
      state.activeSubscriptionsMovement.segments = action.payload.segments;
      state.activeSubscriptionsMovement.summary = action.payload.summary;
      state.activeSubscriptionsMovement.values = action.payload.values;

      state.activeSubscriptionsMovement.isLoading = false;
    },

    cohortExplorerChartStartLoading(state) {
      state.cohortExplorer.isLoading = true;
    },

    cohortExplorerChartHasError(state, action) {
      state.cohortExplorer.isLoading = false;
      state.cohortExplorer.error = action.payload;
    },

    cohortExplorerChartSuccess(state, action) {
      state.cohortExplorer.data = action.payload;
      state.cohortExplorer.isLoading = false;
    },

    cohortExplorerRecoupChartStartLoading(state) {
      state.cohortExplorerRecoup.isLoading = true;
    },

    cohortExplorerRecoupChartHasError(state, action) {
      state.cohortExplorerRecoup.isLoading = false;
      state.cohortExplorerRecoup.error = action.payload;
    },

    cohortExplorerRecoupChartSuccess(state, action) {
      state.cohortExplorerRecoup.data = action.payload;
      state.cohortExplorerRecoup.isLoading = false;
    },

    cohortedInitialConversionChartStartLoading(state) {
      state.cohortedInitialConversion.isLoading = true;
    },

    cohortedInitialConversionChartHasError(state, action) {
      state.cohortedInitialConversion.isLoading = false;
      state.cohortedInitialConversion.error = action.payload;
    },

    cohortedInitialConversionChartSuccess(state, action) {
      const rows = aggregateConversionRows(normalizeCohortRows(action.payload));
      const { segments, values, pointIncomplete } = buildConversionChartData(rows);
      state.cohortedInitialConversion.segments = segments;
      state.cohortedInitialConversion.values = values;
      state.cohortedInitialConversion.rawData = rows;
      state.cohortedInitialConversion.pointIncomplete = pointIncomplete;
      state.cohortedInitialConversion.isLoading = false;
    },

    cohortedTrialConversionChartStartLoading(state) {
      state.cohortedTrialConversion.isLoading = true;
    },

    cohortedTrialConversionChartHasError(state, action) {
      state.cohortedTrialConversion.isLoading = false;
      state.cohortedTrialConversion.error = action.payload;
    },

    cohortedTrialConversionChartSuccess(state, action) {
      const rows = aggregateTrialRows(normalizeCohortRows(action.payload));
      // segments/values are built by the page component's useMemo from rawData
      state.cohortedTrialConversion.segments = [];
      state.cohortedTrialConversion.values = [];
      state.cohortedTrialConversion.rawData = rows;
      state.cohortedTrialConversion.pointIncomplete = rows.map(rowCohortEdgeIncomplete);
      state.cohortedTrialConversion.isLoading = false;
    },

    cohortedIntroOfferConversionChartStartLoading(state) {
      state.cohortedIntroOfferConversion.isLoading = true;
    },

    cohortedIntroOfferConversionChartHasError(state, action) {
      state.cohortedIntroOfferConversion.isLoading = false;
      state.cohortedIntroOfferConversion.error = action.payload;
    },

    cohortedIntroOfferConversionChartSuccess(state, action) {
      const rows = aggregateTrialRows(normalizeCohortRows(action.payload));
      state.cohortedIntroOfferConversion.segments = [];
      state.cohortedIntroOfferConversion.values = [];
      state.cohortedIntroOfferConversion.rawData = rows;
      state.cohortedIntroOfferConversion.pointIncomplete = rows.map(rowCohortEdgeIncomplete);
      state.cohortedIntroOfferConversion.isLoading = false;
    },

    cohortedConversionToPayingChartStartLoading(state) {
      state.cohortedConversionToPaying.isLoading = true;
    },

    cohortedConversionToPayingChartHasError(state, action) {
      state.cohortedConversionToPaying.isLoading = false;
      state.cohortedConversionToPaying.error = action.payload;
    },

    cohortedConversionToPayingChartSuccess(state, action) {
      const rows = aggregateConversionRows(normalizeCohortRows(action.payload));
      const { segments, values, pointIncomplete } = buildConversionChartData(rows);
      state.cohortedConversionToPaying.segments = segments;
      state.cohortedConversionToPaying.values = values;
      state.cohortedConversionToPaying.rawData = rows;
      state.cohortedConversionToPaying.pointIncomplete = pointIncomplete;
      state.cohortedConversionToPaying.isLoading = false;
    },

    cohortedReactivationChartStartLoading(state) {
      state.cohortedReactivation.isLoading = true;
    },

    cohortedReactivationChartHasError(state, action) {
      state.cohortedReactivation.isLoading = false;
      state.cohortedReactivation.error = action.payload;
    },

    cohortedReactivationChartSuccess(state, action) {
      const rows = aggregateConversionRows(normalizeCohortRows(action.payload));
      const { segments, values, pointIncomplete } = buildConversionChartData(rows);
      state.cohortedReactivation.segments = segments;
      state.cohortedReactivation.values = values;
      state.cohortedReactivation.rawData = rows;
      state.cohortedReactivation.pointIncomplete = pointIncomplete;
      state.cohortedReactivation.isLoading = false;
    },

    updateConversionTimeframe(state, action) {
      const { chartKey, value } = action.payload;
      state[chartKey].conversionTimeframe = value;
    },

    updateCohortBasis(state, action) {
      const { chartKey, value } = action.payload;
      state[chartKey].cohortBasis = value;
    },

    replaceFilters(state, action) {
      const { chartKey, segment, ...filters } = action.payload;
      // Replace entire filters object (no merge) — used by sidebar Apply
      const newFilters = {};
      Object.keys(filters).forEach((key) => {
        const v = filters[key];
        if (v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0)) {
          newFilters[key] = v;
        }
      });
      state[chartKey].filters = newFilters;
      if (segment !== undefined) {
        state[chartKey].segment = segment || null;
      }
    },

    autoRenewStatusChartStartLoading(state) {
      state.autoRenewStatus.isLoading = true;
    },

    autoRenewStatusChartHasError(state, action) {
      state.autoRenewStatus.isLoading = false;
      state.autoRenewStatus.error = action.payload;
    },

    autoRenewStatusChartSuccess(state, action) {
      state.autoRenewStatus.values = action.payload;

      state.autoRenewStatus.isLoading = false;
    },

    subscriptionRetentionChartStartLoading(state) {
      state.subscriptionRetention.isLoading = true;
    },

    subscriptionRetentionChartHasError(state, action) {
      state.subscriptionRetention.isLoading = false;
      state.subscriptionRetention.error = action.payload;
    },

    subscriptionRetentionChartSuccess(state, action) {
      state.subscriptionRetention.segment = action.payload.segment;
      state.subscriptionRetention.segments = action.payload.segments;
      state.subscriptionRetention.summary = action.payload.summary;
      state.subscriptionRetention.values = action.payload.values;
      state.subscriptionRetention.percentageValues = action.payload.percentageValues;
      state.subscriptionRetention.extras = action.payload.extras;

      state.subscriptionRetention.isLoading = false;
    },

    subscriptionEventsChartStartLoading(state) {
      state.subscriptionEvents.isLoading = true;
    },

    subscriptionEventsChartHasError(state, action) {
      state.subscriptionEvents.isLoading = false;
      state.subscriptionEvents.error = action.payload;
    },

    subscriptionEventsChartSuccess(state, action) {
      state.subscriptionEvents.segment = action.payload.eventChart.segment;
      state.subscriptionEvents.segments = action.payload.eventChart.segments;
      state.subscriptionEvents.summary = action.payload.eventChart.summary;
      state.subscriptionEvents.values = action.payload.eventChart.values;

      state.subscriptionEvents.extras = {
        segment: action.payload.subEventChart.segment,
        segments: action.payload.subEventChart.segments,
        summary: action.payload.subEventChart.summary,
        values: action.payload.subEventChart.values,
      };
      // Older servers don't send it; the page falls back to the two separate tables.
      state.subscriptionEvents.breakdown = action.payload.eventBreakdown ?? null;

      state.subscriptionEvents.isLoading = false;
    },

    // Ad ROI reducers
    trafficQualityStartLoading(state) {
      state.trafficQuality.isLoading = true;
      state.trafficQuality.error = null;
    },

    trafficQualityHasError(state, action) {
      state.trafficQuality.isLoading = false;
      state.trafficQuality.error = action.payload;
    },

    trafficQualitySuccess(state, action) {
      state.trafficQuality.data = action.payload || [];
      state.trafficQuality.isLoading = false;
      state.trafficQuality.error = null;
    },

    adROIStartLoading(state) {
      state.adROI.isLoading = true;
    },

    adROIHasError(state, action) {
      state.adROI.isLoading = false;
      state.adROI.error = action.payload;
    },

    adROISuccess(state, action) {
      // action.payload is directly the data array
      state.adROI.data = action.payload || [];
      state.adROI.isLoading = false;
      state.adROI.error = null;
    },

    adROIFilterOptionsSuccess(state, action) {
      state.adROI.filterOptions = action.payload;
    },

    adROISyncSuccess(state) {
      state.adROI.isLoading = false;
    },

    adROISummarySuccess(state, action) {
      state.adROI.summary = action.payload || null;
    },

    // Ad ROI Chart reducers
    adROIChartStartLoading(state) {
      state.adROI.chart.isLoading = true;
    },

    adROIChartHasError(state, action) {
      state.adROI.chart.isLoading = false;
      state.adROI.chart.error = action.payload;
    },

    adROIChartSuccess(state, action) {
      state.adROI.chart.resolution = action.payload.resolution;
      state.adROI.chart.segments = action.payload.segments;
      state.adROI.chart.summary = action.payload.summary;
      state.adROI.chart.values = action.payload.values;
      state.adROI.chart.isLoading = false;
      state.adROI.chart.error = null;
    },

    updateAdROIChartResolution(state, action) {
      state.adROI.chart.resolution = action.payload;
    },

    // Cost ETL reducers
    costETLStartLoading(state) {
      state.costETL.isLoading = true;
    },

    costETLHasError(state, action) {
      state.costETL.isLoading = false;
      state.costETL.error = action.payload;
    },

    costETLSuccess(state, action) {
      state.costETL.data = action.payload || [];
      state.costETL.isLoading = false;
      state.costETL.error = null;
    },

    costETLFilterOptionsSuccess(state, action) {
      state.costETL.filterOptions = action.payload;
    },

    costETLAvailableDatesSuccess(state, action) {
      state.costETL.availableDates = action.payload.dates || [];
    },

    costETLSyncSuccess(state) {
      state.costETL.isLoading = false;
    },

    costETLSummarySuccess(state, action) {
      state.costETL.summary = action.payload || null;
    },

    // Cost ETL Chart reducers
    costETLChartStartLoading(state) {
      state.costETL.chart.isLoading = true;
    },

    costETLChartHasError(state, action) {
      state.costETL.chart.isLoading = false;
      state.costETL.chart.error = action.payload;
    },

    costETLChartSuccess(state, action) {
      state.costETL.chart.resolution = action.payload.resolution;
      state.costETL.chart.segments = action.payload.segments;
      state.costETL.chart.summary = action.payload.summary;
      state.costETL.chart.values = action.payload.values;
      state.costETL.chart.isLoading = false;
      state.costETL.chart.error = null;
    },

    updateCostETLChartResolution(state, action) {
      state.costETL.chart.resolution = action.payload;
    },
  },
});

export default slice.reducer;

export const {
  resetFilters,
  replaceFilters,
  updateResolution,
  updateStartDate,
  updateFilters,
  updateEndDate,
  updatePeriod,
  updateCancellationType,
  updateSubscriptionStatusMeasure,
  updateConversionTimeframe,
  updateCohortBasis,
} = slice.actions;

export function getMetaChart(appId, { force = false } = {}) {
  return async (dispatch, getState) => {
    const { meta } = getState().revenueManagementCharts;
    // Meta (products/stores/countries/…) is app-scoped and effectively static per app. Skip the
    // request when we already have it (or it's already in flight) for this app — callers across
    // charts and list filters share one cached copy instead of refetching on every mount.
    if (!force && String(meta.appId) === String(appId) && (meta.loaded || meta.isLoading)) return;
    dispatch(slice.actions.metaChartStartLoading(appId));
    try {
      const response = await axios.get(`/revenue-management/${appId}/charts/meta`);
      dispatch(slice.actions.metaChartSuccess({ ...response, appId }));
    } catch (error) {
      dispatch(slice.actions.metaChartHasError(error.message));
    }
  };
}

export function getRevenueChart(appId, startDate, endDate, resolution, filterOption) {
  return async (dispatch) => {
    dispatch(slice.actions.revenueChartStartLoading());
    try {
      const response = await axios.get(
        `/revenue-management/${appId}/charts/revenue?resolution=${resolution}&startDate=${startDate}&endDate=${endDate}&filters=${JSON.stringify(
          filterOption,
        )}`,
      );
      dispatch(slice.actions.revenueChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.revenueChartHasError(error.message));
    }
  };
}

export function getMRRChart(appId, startDate, endDate, resolution, filterOption, revenueType = 'gross') {
  return async (dispatch) => {
    dispatch(slice.actions.mrrChartStartLoading());
    try {
      const response = await axios.get(
        `/revenue-management/${appId}/charts/mrr?resolution=${resolution}&startDate=${startDate}&endDate=${endDate}&revenueType=${revenueType}&filters=${JSON.stringify(
          filterOption,
        )}`,
      );
      dispatch(slice.actions.mrrChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.mrrChartHasError(error.message));
    }
  };
}

export function getARRChart(appId, startDate, endDate, resolution, filterOption) {
  return async (dispatch) => {
    dispatch(slice.actions.arrChartStartLoading());
    try {
      // Try ARR endpoint first, if it doesn't exist, fall back to MRR and multiply by 12
      let response;
      try {
        response = await axios.get(
          `/revenue-management/${appId}/charts/arr?resolution=${resolution}&startDate=${startDate}&endDate=${endDate}&filters=${JSON.stringify(
            filterOption,
          )}`,
        );
      } catch (arrError) {
        // If ARR endpoint doesn't exist, get MRR and multiply by 12
        const mrrResponse = await axios.get(
          `/revenue-management/${appId}/charts/mrr?resolution=${resolution}&startDate=${startDate}&endDate=${endDate}&filters=${JSON.stringify(
            filterOption,
          )}`,
        );

        // Multiply MRR values by 12 to get ARR
        const arrData = {
          ...mrrResponse.data,
          values: mrrResponse.data.values?.map((row) => {
            if (Array.isArray(row)) {
              return [row[0], ...row.slice(1).map((val) => (typeof val === 'number' ? val * 12 : val))];
            }
            return row;
          }),
          summary: mrrResponse.data.summary
            ? {
                total: Object.fromEntries(
                  Object.entries(mrrResponse.data.summary.total || {}).map(([key, val]) => [
                    key,
                    typeof val === 'number' ? val * 12 : val,
                  ]),
                ),
                average: Object.fromEntries(
                  Object.entries(mrrResponse.data.summary.average || {}).map(([key, val]) => [
                    key,
                    typeof val === 'number' ? val * 12 : val,
                  ]),
                ),
              }
            : null,
        };
        response = { data: arrData };
      }
      dispatch(slice.actions.arrChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.arrChartHasError(error.message));
    }
  };
}

export function getChurnChart(appId, startDate, endDate, resolution, filterOption) {
  return async (dispatch) => {
    dispatch(slice.actions.churnChartStartLoading());
    try {
      const response = await axios.get(
        `/revenue-management/${appId}/charts/churn?resolution=${resolution}&startDate=${startDate}&endDate=${endDate}&filters=${JSON.stringify(
          filterOption,
        )}`,
      );
      dispatch(slice.actions.churnChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.churnChartHasError(error.message));
    }
  };
}

export function getRefundRateChart(appId, startDate, endDate, resolution, filterOption) {
  return async (dispatch) => {
    dispatch(slice.actions.refundRateChartStartLoading());
    try {
      const response = await axios.get(
        `/revenue-management/${appId}/charts/refund-rate?resolution=${resolution}&startDate=${startDate}&endDate=${endDate}&filters=${JSON.stringify(
          filterOption,
        )}`,
      );
      dispatch(slice.actions.refundRateChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.refundRateChartHasError(error.message));
    }
  };
}

export function getAppStoreRefundRequestsChart(appId, startDate, endDate, resolution, filterOption) {
  return async (dispatch) => {
    dispatch(slice.actions.appStoreRefundRequestsChartStartLoading());
    try {
      const response = await axios.get(
        `/revenue-management/${appId}/charts/app-store-refund-requests?resolution=${resolution}&startDate=${startDate}&endDate=${endDate}&filters=${JSON.stringify(
          filterOption,
        )}`,
      );
      dispatch(slice.actions.appStoreRefundRequestsChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.appStoreRefundRequestsChartHasError(error.message));
    }
  };
}

export function getPlayStoreCancelReasonsChart(appId, startDate, endDate, resolution, filterOption) {
  return async (dispatch) => {
    dispatch(slice.actions.playStoreCancelReasonsChartStartLoading());
    try {
      const response = await axios.get(
        `/revenue-management/${appId}/charts/play-store-cancel-reasons?resolution=${resolution}&startDate=${startDate}&endDate=${endDate}&filters=${JSON.stringify(
          filterOption,
        )}`,
      );
      dispatch(slice.actions.playStoreCancelReasonsChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.playStoreCancelReasonsChartHasError(error.message));
    }
  };
}

export function getCancellationTimingChart(appId, startDate, endDate, cancellationType, filterOption) {
  return async (dispatch) => {
    dispatch(slice.actions.cancellationTimingChartStartLoading());
    try {
      const response = await axios.get(
        `/revenue-management/${appId}/charts/cancellation-timing?startDate=${startDate}&endDate=${endDate}&cancellationType=${cancellationType}&filters=${JSON.stringify(
          filterOption,
        )}`,
      );
      dispatch(slice.actions.cancellationTimingChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.cancellationTimingChartHasError(error.message));
    }
  };
}

export function getSubscriptionStatusChart(appId, startDate, endDate, resolution, measure, filterOption) {
  return async (dispatch) => {
    dispatch(slice.actions.subscriptionStatusChartStartLoading());
    try {
      const response = await axios.get(
        `/revenue-management/${appId}/charts/subscription-status?startDate=${startDate}&endDate=${endDate}&resolution=${resolution}&measure=${measure}&filters=${JSON.stringify(
          filterOption || {},
        )}`,
      );
      dispatch(slice.actions.subscriptionStatusChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.subscriptionStatusChartHasError(error?.message || 'Failed to load chart'));
    }
  };
}

export function getConversionToPayingChart(appId, startDate, endDate, resolution, filterOption) {
  return async (dispatch) => {
    dispatch(slice.actions.conversionToPayingChartStartLoading());
    try {
      const response = await axios.get(
        `/revenue-management/${appId}/charts/conversion-to-paying?resolution=${resolution}&startDate=${startDate}&endDate=${endDate}&filters=${JSON.stringify(
          filterOption,
        )}`,
      );
      dispatch(slice.actions.conversionToPayingChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.conversionToPayingChartHasError(error.message));
    }
  };
}

export function getInitialConversionChart(appId, startDate, endDate, resolution, filterOption) {
  return async (dispatch) => {
    dispatch(slice.actions.initialConversionChartStartLoading());
    try {
      const response = await axios.get(
        `/revenue-management/${appId}/charts/initial-conversion?resolution=${resolution}&startDate=${startDate}&endDate=${endDate}&filters=${JSON.stringify(
          filterOption,
        )}`,
      );
      dispatch(slice.actions.initialConversionChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.initialConversionChartHasError(error.message));
    }
  };
}

export function getTrialConversionChart(appId, startDate, endDate, resolution, filterOption) {
  return async (dispatch) => {
    dispatch(slice.actions.trialConversionChartStartLoading());
    try {
      const response = await axios.get(
        `/revenue-management/${appId}/charts/trial-conversion?resolution=${resolution}&startDate=${startDate}&endDate=${endDate}&filters=${JSON.stringify(
          filterOption,
        )}`,
      );
      dispatch(slice.actions.trialConversionChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.trialConversionChartHasError(error.message));
    }
  };
}

export function getOfferConversionChart(appId, startDate, endDate, resolution, filterOption) {
  return async (dispatch) => {
    dispatch(slice.actions.offerConversionChartStartLoading());
    try {
      const response = await axios.get(
        `/revenue-management/${appId}/charts/offer-conversion?resolution=${resolution}&startDate=${startDate}&endDate=${endDate}&filters=${JSON.stringify(
          filterOption,
        )}`,
      );
      dispatch(slice.actions.offerConversionChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.offerConversionChartHasError(error.message));
    }
  };
}

export function getReactivationChart(appId, startDate, endDate, resolution, filterOption) {
  return async (dispatch) => {
    dispatch(slice.actions.reactivationChartStartLoading());
    try {
      const response = await axios.get(
        `/revenue-management/${appId}/charts/reactivation?resolution=${resolution}&startDate=${startDate}&endDate=${endDate}&filters=${JSON.stringify(
          filterOption,
        )}`,
      );
      dispatch(slice.actions.reactivationChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.reactivationChartHasError(error.message));
    }
  };
}

export function getActiveTrialsChart(appId, startDate, endDate, resolution, filterOption) {
  return async (dispatch) => {
    dispatch(slice.actions.activeTrialsChartStartLoading());
    try {
      const response = await axios.get(
        `/revenue-management/${appId}/charts/active-trials?resolution=${resolution}&startDate=${startDate}&endDate=${endDate}&filters=${JSON.stringify(
          filterOption,
        )}`,
      );
      dispatch(slice.actions.activeTrialsChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.activeTrialsChartHasError(error));
    }
  };
}

export function getNewTrialsChart(appId, startDate, endDate, resolution, filterOption) {
  return async (dispatch) => {
    dispatch(slice.actions.newTrialsChartStartLoading());
    try {
      const response = await axios.get(
        `/revenue-management/${appId}/charts/new-trials?resolution=${resolution}&startDate=${startDate}&endDate=${endDate}&filters=${JSON.stringify(
          filterOption,
        )}`,
      );
      dispatch(slice.actions.newTrialsChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.newTrialsChartHasError(error));
    }
  };
}

export function getActiveTrialsMovementChart(appId, startDate, endDate, resolution, filterOption) {
  return async (dispatch) => {
    dispatch(slice.actions.activeTrialsMovementChartStartLoading());
    try {
      const response = await axios.get(
        `/revenue-management/${appId}/charts/active-trials-movement?resolution=${resolution}&startDate=${startDate}&endDate=${endDate}&filters=${JSON.stringify(
          filterOption,
        )}`,
      );
      dispatch(slice.actions.activeTrialsMovementChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.activeTrialsMovementChartHasError(error));
    }
  };
}

export function getActiveSubscribersChart(appId, startDate, endDate, resolution, filterOption) {
  return async (dispatch) => {
    dispatch(slice.actions.activeSubscribersChartStartLoading());
    try {
      const response = await axios.get(
        `/revenue-management/${appId}/charts/active-subscribers?resolution=${resolution}&startDate=${startDate}&endDate=${endDate}&filters=${JSON.stringify(
          filterOption,
        )}`,
      );

      dispatch(slice.actions.activeSubscribersChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.activeSubscribersChartHasError(error.message));
    }
  };
}

export function getActiveSubscriptionsMovementChart(appId, startDate, endDate, resolution, filterOption) {
  return async (dispatch) => {
    dispatch(slice.actions.activeSubscriptionsMovementChartStartLoading());
    try {
      const response = await axios.get(
        `/revenue-management/${appId}/charts/active-subscriptions-movement?resolution=${resolution}&startDate=${startDate}&endDate=${endDate}&filters=${JSON.stringify(
          filterOption,
        )}`,
      );
      dispatch(slice.actions.activeSubscriptionsMovementChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.activeSubscriptionsMovementChartHasError(error));
    }
  };
}

export function getCohortExplorerChart(
  appId,
  startDate,
  endDate,
  resolution,
  cohortDefinition,
  measure,
  revenueType,
  filters = {},
) {
  return async (dispatch) => {
    dispatch(slice.actions.cohortExplorerChartStartLoading());
    try {
      // Build query string similar to revenue chart
      const filtersParam = filters && Object.keys(filters).length > 0 ? `&filters=${JSON.stringify(filters)}` : '';
      const response = await axios.get(
        `/revenue-management/${appId}/charts/cohort-explorer?startDate=${startDate}&endDate=${endDate}&resolution=${resolution}&cohortDefinition=${cohortDefinition}&measure=${measure}&revenueType=${revenueType}${filtersParam}`,
      );
      dispatch(slice.actions.cohortExplorerChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.cohortExplorerChartHasError(error.message));
    }
  };
}

export function getCohortExplorerRecoupChart(
  appId,
  startDate,
  endDate,
  resolution,
  cohortDefinition,
  revenueType,
  filters = {},
) {
  return async (dispatch) => {
    dispatch(slice.actions.cohortExplorerRecoupChartStartLoading());
    try {
      const filtersParam = filters && Object.keys(filters).length > 0 ? `&filters=${JSON.stringify(filters)}` : '';
      const response = await axios.get(
        `/revenue-management/${appId}/charts/cohort-explorer-recoup?startDate=${startDate}&endDate=${endDate}&resolution=${resolution}&cohortDefinition=${cohortDefinition}&revenueType=${revenueType}${filtersParam}`,
      );
      dispatch(slice.actions.cohortExplorerRecoupChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.cohortExplorerRecoupChartHasError(error.message));
    }
  };
}

export function getAutoRenewStatusChart(appId) {
  return async (dispatch) => {
    dispatch(slice.actions.autoRenewStatusChartStartLoading());
    try {
      const response = await axios.get(`/revenue-management/${appId}/charts/auto-renew-status`);
      dispatch(slice.actions.autoRenewStatusChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.autoRenewStatusChartHasError(error.message));
    }
  };
}

export function getSubscriptionRetentionChart(appId, startDate, endDate, resolution, period, filterOption) {
  return async (dispatch) => {
    dispatch(slice.actions.subscriptionRetentionChartStartLoading());
    try {
      const response = await axios.get(
        `/revenue-management/${appId}/charts/subscription-retention?resolution=${resolution}&startDate=${startDate}&endDate=${endDate}&period=${period}&filters=${JSON.stringify(
          filterOption,
        )}`,
      );
      dispatch(slice.actions.subscriptionRetentionChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.subscriptionRetentionChartHasError(error.message));
    }
  };
}

export function getSubscriptionEventsChart(appId, startDate, endDate, resolution, filterOption) {
  return async (dispatch) => {
    dispatch(slice.actions.subscriptionEventsChartStartLoading());
    try {
      const response = await axios.get(
        `/revenue-management/${appId}/charts/subscription-events?resolution=${resolution}&startDate=${startDate}&endDate=${endDate}&filters=${JSON.stringify(
          filterOption,
        )}`,
      );
      dispatch(slice.actions.subscriptionEventsChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.subscriptionEventsChartHasError(error.message));
    }
  };
}

// Ad ROI Actions
const appendFilterParam = (params, key, value) => {
  if (!value) return;
  if (Array.isArray(value)) {
    value.forEach((v) => params.append(key, v));
  } else {
    params.append(key, value);
  }
};

export function getTrafficQuality(appId, startDate, endDate) {
  return async (dispatch) => {
    dispatch(slice.actions.trafficQualityStartLoading());
    try {
      // The axios interceptor already unwraps { success, data } and hands back the payload itself,
      // so `response` IS the array. Reaching for response.data.data unwrapped it a second time and
      // always landed on [] — which the page rendered as "No media source activity in this period"
      // while the endpoint was returning every source correctly.
      const response = await axios.get(
        `/revenue-management/${appId}/charts/traffic-quality?startDate=${startDate}&endDate=${endDate}`
      );
      dispatch(slice.actions.trafficQualitySuccess(response ?? []));
    } catch (error) {
      dispatch(slice.actions.trafficQualityHasError(error.message));
    }
  };
}

export function getAdROIReport(appId, startDate, endDate, filters = {}, segment = null) {
  return async (dispatch) => {
    dispatch(slice.actions.adROIStartLoading());
    try {
      const params = new URLSearchParams({ appId, startDate, endDate });
      appendFilterParam(params, 'country', filters.country);
      appendFilterParam(params, 'mediaSource', filters.mediaSource);
      appendFilterParam(params, 'campaign', filters.campaign);
      appendFilterParam(params, 'platform', filters.platform);
      if (segment) params.append('segment', segment);

      const response = await axios.get(`/ad-roi/report?${params.toString()}`);
      // Backend should return grouped data when segment is provided
      // If segment is provided, backend should perform GROUP BY and return aggregated data
      dispatch(slice.actions.adROISuccess(response.data || response));
    } catch (error) {
      dispatch(slice.actions.adROIHasError(error.message));
    }
  };
}

export function getAdROIFilterOptions(appId, startDate, endDate) {
  return async (dispatch) => {
    try {
      const response = await axios.get(`/ad-roi/filters?appId=${appId}&startDate=${startDate}&endDate=${endDate}`);
      dispatch(slice.actions.adROIFilterOptionsSuccess(response));
    } catch (error) {
      console.error('Failed to fetch Ad ROI filter options:', error);
    }
  };
}

export function syncAdROICostData(appId, startDate, endDate) {
  return async (dispatch) => {
    dispatch(slice.actions.adROIStartLoading());
    try {
      await axios.post(`/ad-roi/sync`, { appId, startDate, endDate });
      dispatch(slice.actions.adROISyncSuccess());
      // Refresh the report and summary after sync
      dispatch(getAdROIReport(appId, startDate, endDate, {}, null));
      dispatch(getAdROISummary(appId, startDate, endDate, {}));
      dispatch(getAdROIFilterOptions(appId, startDate, endDate));
    } catch (error) {
      dispatch(slice.actions.adROIHasError(error.message));
    }
  };
}

export function getAdROISummary(appId, startDate, endDate, filters = {}) {
  return async (dispatch) => {
    try {
      const params = new URLSearchParams({ appId, startDate, endDate });
      appendFilterParam(params, 'country', filters.country);
      appendFilterParam(params, 'mediaSource', filters.mediaSource);
      appendFilterParam(params, 'platform', filters.platform);

      const response = await axios.get(`/ad-roi/summary?${params.toString()}`);
      dispatch(slice.actions.adROISummarySuccess(response));
    } catch (error) {
      console.error('Failed to fetch Ad ROI summary:', error);
    }
  };
}

export function getAdROIChart(appId, startDate, endDate, resolution = 'days', filters = {}) {
  return async (dispatch) => {
    dispatch(slice.actions.adROIChartStartLoading());
    try {
      const params = new URLSearchParams({ appId, startDate, endDate, resolution });
      appendFilterParam(params, 'country', filters.country);
      appendFilterParam(params, 'mediaSource', filters.mediaSource);
      appendFilterParam(params, 'campaign', filters.campaign);
      appendFilterParam(params, 'platform', filters.platform);

      const response = await axios.get(`/ad-roi/chart?${params.toString()}`);
      dispatch(slice.actions.adROIChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.adROIChartHasError(error.message));
    }
  };
}

export const { updateAdROIChartResolution, updateCostETLChartResolution } = slice.actions;

// Cost ETL Actions
export function getCostETLReport(startDate, endDate, filters = {}, segment = null) {
  return async (dispatch) => {
    dispatch(slice.actions.costETLStartLoading());
    try {
      const params = new URLSearchParams({ startDate, endDate });
      if (filters.appId) params.append('appId', filters.appId);
      appendFilterParam(params, 'country', filters.country);
      appendFilterParam(params, 'mediaSource', filters.mediaSource);
      appendFilterParam(params, 'campaign', filters.campaign);
      appendFilterParam(params, 'platform', filters.platform);
      if (segment) params.append('segment', segment);

      const response = await axios.get(`/cost-etl/report?${params.toString()}`);
      dispatch(slice.actions.costETLSuccess(response.data || response));
    } catch (error) {
      dispatch(slice.actions.costETLHasError(error.message));
    }
  };
}

export function getCostETLFilterOptions(appId, startDate, endDate) {
  return async (dispatch) => {
    try {
      const params = new URLSearchParams({ appId, startDate, endDate });
      const response = await axios.get(`/cost-etl/filters?${params.toString()}`);
      dispatch(slice.actions.costETLFilterOptionsSuccess(response));
    } catch (error) {
      console.error('Failed to fetch Cost ETL filter options:', error);
    }
  };
}

export function getCostETLAvailableDates(appId, reportType = 'geo') {
  return async (dispatch) => {
    try {
      const params = new URLSearchParams({ appId, reportType });
      const response = await axios.get(`/cost-etl/available-dates?${params.toString()}`);
      dispatch(slice.actions.costETLAvailableDatesSuccess(response));
    } catch (error) {
      console.error('Failed to fetch Cost ETL available dates:', error);
    }
  };
}

export function syncCostETLData(appId, startDate, endDate, reportType = 'geo') {
  return async (dispatch) => {
    dispatch(slice.actions.costETLStartLoading());
    try {
      await axios.post(`/cost-etl/sync`, { appId, startDate, endDate, reportType });
      dispatch(slice.actions.costETLSyncSuccess());
      const filtersWithApp = { appId };
      dispatch(getCostETLReport(startDate, endDate, filtersWithApp, null));
      dispatch(getCostETLSummary(startDate, endDate, filtersWithApp));
      dispatch(getCostETLFilterOptions(appId, startDate, endDate));
    } catch (error) {
      dispatch(slice.actions.costETLHasError(error.message));
    }
  };
}

export function getCostETLSummary(startDate, endDate, filters = {}) {
  return async (dispatch) => {
    try {
      const params = new URLSearchParams({ startDate, endDate });
      if (filters.appId) params.append('appId', filters.appId);
      appendFilterParam(params, 'country', filters.country);
      appendFilterParam(params, 'mediaSource', filters.mediaSource);
      appendFilterParam(params, 'campaign', filters.campaign);
      appendFilterParam(params, 'platform', filters.platform);

      const response = await axios.get(`/cost-etl/summary?${params.toString()}`);
      dispatch(slice.actions.costETLSummarySuccess(response));
    } catch (error) {
      console.error('Failed to fetch Cost ETL summary:', error);
    }
  };
}

export function getCostETLChart(startDate, endDate, resolution = 'days', filters = {}) {
  return async (dispatch) => {
    dispatch(slice.actions.costETLChartStartLoading());
    try {
      const params = new URLSearchParams({ startDate, endDate, resolution });
      if (filters.appId) params.append('appId', filters.appId);
      appendFilterParam(params, 'country', filters.country);
      appendFilterParam(params, 'mediaSource', filters.mediaSource);
      appendFilterParam(params, 'campaign', filters.campaign);
      appendFilterParam(params, 'platform', filters.platform);

      const response = await axios.get(`/cost-etl/chart?${params.toString()}`);
      dispatch(slice.actions.costETLChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.costETLChartHasError(error.message));
    }
  };
}

export function getCohortedInitialConversionChart(appId, startDate, endDate, resolution, conversionTimeframe, filters) {
  return async (dispatch) => {
    dispatch(slice.actions.cohortedInitialConversionChartStartLoading());
    try {
      const params = new URLSearchParams({ startDate, endDate, resolution, conversionTimeframe });
      if (filters && Object.keys(filters).length > 0) {
        params.append('filters', JSON.stringify(filters));
      }
      const response = await axios.get(
        `/revenue-management/${appId}/charts/cohorted-initial-conversion?${params.toString()}`,
      );
      dispatch(slice.actions.cohortedInitialConversionChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.cohortedInitialConversionChartHasError(error.message));
    }
  };
}

export function getCohortedTrialConversionChart(appId, startDate, endDate, resolution, filters) {
  return async (dispatch) => {
    dispatch(slice.actions.cohortedTrialConversionChartStartLoading());
    try {
      const params = new URLSearchParams({ startDate, endDate, resolution });
      if (filters && Object.keys(filters).length > 0) {
        params.append('filters', JSON.stringify(filters));
      }
      const response = await axios.get(
        `/revenue-management/${appId}/charts/cohorted-trial-conversion?${params.toString()}`,
      );
      dispatch(slice.actions.cohortedTrialConversionChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.cohortedTrialConversionChartHasError(error.message));
    }
  };
}

export function getCohortedIntroOfferConversionChart(appId, startDate, endDate, resolution, filters) {
  return async (dispatch) => {
    dispatch(slice.actions.cohortedIntroOfferConversionChartStartLoading());
    try {
      const params = new URLSearchParams({ startDate, endDate, resolution });
      if (filters && Object.keys(filters).length > 0) {
        params.append('filters', JSON.stringify(filters));
      }
      const response = await axios.get(
        `/revenue-management/${appId}/charts/cohorted-intro-conversion?${params.toString()}`,
      );
      dispatch(slice.actions.cohortedIntroOfferConversionChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.cohortedIntroOfferConversionChartHasError(error.message));
    }
  };
}

export function getCohortedConversionToPayingChart(
  appId,
  startDate,
  endDate,
  resolution,
  conversionTimeframe,
  filters,
) {
  return async (dispatch) => {
    dispatch(slice.actions.cohortedConversionToPayingChartStartLoading());
    try {
      const params = new URLSearchParams({ startDate, endDate, resolution, conversionTimeframe });
      if (filters && Object.keys(filters).length > 0) {
        params.append('filters', JSON.stringify(filters));
      }
      const response = await axios.get(
        `/revenue-management/${appId}/charts/cohorted-conversion-to-paying?${params.toString()}`,
      );
      dispatch(slice.actions.cohortedConversionToPayingChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.cohortedConversionToPayingChartHasError(error.message));
    }
  };
}

export function getCohortedReactivationChart(
  appId,
  startDate,
  endDate,
  resolution,
  conversionTimeframe,
  cohortBasis,
  filters,
) {
  return async (dispatch) => {
    dispatch(slice.actions.cohortedReactivationChartStartLoading());
    try {
      const params = new URLSearchParams({ startDate, endDate, resolution, conversionTimeframe, cohortBasis });
      if (filters && Object.keys(filters).length > 0) {
        params.append('filters', JSON.stringify(filters));
      }
      const response = await axios.get(`/revenue-management/${appId}/charts/cohorted-reactivation?${params.toString()}`);
      dispatch(slice.actions.cohortedReactivationChartSuccess(response));
    } catch (error) {
      dispatch(slice.actions.cohortedReactivationChartHasError(error.message));
    }
  };
}
